import React, { ReactNode } from 'react';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import { EPDBasedOnUnitEnum, LCAModules } from 'types/types';
import { Placeholder } from './StyledGrid';
import formatToFixed8OrToInteger from 'util/formatToFixed8OrToInteger';
import { ColumnBodyOptions } from 'primereact/column';

export type LcaGridNote = { note: ReactNode; description: ReactNode };

export function columnTotal<T>(columnName: keyof T, rows: T[] | undefined | null) {
  const total =
    rows?.reduce((a: number, b: any) => {
      const value = b[columnName] as number | undefined;
      return a + (value ? value : 0);
    }, 0) ?? 0;

  return formatToFixed8OrToInteger(total);
}

export const mandatoryBodyTemplate = (value: any, error?: any) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>{value}</span>
      {(error || !value) && <TooltipErrorIcon content={error || 'Required'} />}
    </div>
  );
};

export const validatedBodyTemplateWithPlaceholder = (value: any, placeholder?: string, error?: any) => {
  if (value && !error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{value}</span>
      </div>
    );
  }

  if (value && error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{value}</span>
        <TooltipErrorIcon content={error || 'Required'} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Placeholder>{placeholder}</Placeholder>
      <TooltipErrorIcon content={error || 'Required'} />
    </div>
  );
};

export const textCellBody =
  (displaySettings?: { placeholder?: React.ReactNode; format?(value: string): React.ReactNode }) =>
  (data: any, options: ColumnBodyOptions) => {
    const rowValue = data[options.field];
    if (rowValue) {
      const formatterFunction = displaySettings?.format;
      return formatterFunction ? formatterFunction(rowValue) : rowValue;
    }
    return displaySettings?.placeholder && <Placeholder>{displaySettings.placeholder}</Placeholder>;
  };

export const numericCellBody =
  (displaySettings?: { placeholder?: React.ReactNode; format?(value: number): React.ReactNode }) =>
  (data: any, options: ColumnBodyOptions) => {
    const rowValue = data[options.field];
    if (typeof rowValue === 'number') {
      const formatterFunction = displaySettings?.format || formatToFixed8OrToInteger;
      return formatterFunction(rowValue);
    }

    return displaySettings?.placeholder && <Placeholder>{displaySettings.placeholder}</Placeholder>;
  };

export const validatedCellTemplate = (value: any, error?: any, placeholder?: string) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
      <span>{hasValue ? value : <Placeholder>{placeholder}</Placeholder>}</span>
      {error && <TooltipErrorIcon content={error} />}
    </div>
  );
};

export const cellTotalTemplate = (fieldName: string, rows: any, errors?: any) =>
  validatedCellTemplate(columnTotal(fieldName, rows), errors?.[`${fieldName}Total`]);

export const validatedCellPercentTemplate = (value: any, error?: any) => {
  return (
    <div style={{ display: 'flex', position: 'relative' }}>
      <span style={{ marginRight: 5 }}>{value}%</span>
      {error && <TooltipErrorIcon content={error} />}
    </div>
  );
};

export const cellTotalPercentTemplate = (fieldName: string, rows: any, errors?: any) => {
  return validatedCellPercentTemplate(columnTotal(fieldName, rows), errors?.[`${fieldName}Total`]);
};

export const emptyLCAModuleValues: LCAModules<string> = {
  a1: '',
  a2: '',
  a3: '',
  a1A2A3: '',
  a4: '',
  a5: '',
  b1: '',
  b2: '',
  b3: '',
  b4: '',
  b5: '',
  b6: '',
  b7: '',
  c1: '',
  c2: '',
  c3: '',
  c4: '',
  d: '',
  variation: '',
};

export const getBiogenicCarbonWeightHeader = (epdBasedOnUnit?: EPDBasedOnUnitEnum) => {
  switch (epdBasedOnUnit) {
    case EPDBasedOnUnitEnum.DeclaredUnit:
      return (
        <span>
          Biogenic material<sup>1</sup>, kg C/declared unit<sup>2</sup>
        </span>
      );
    case EPDBasedOnUnitEnum.FunctionalUnit:
      return (
        <span>
          Biogenic material<sup>1</sup>, kg C/product<sup>2</sup>
        </span>
      );
    default:
      return (
        <span>
          Biogenic material<sup>1</sup>, kg C/declared unit or product<sup>2</sup>
        </span>
      );
  }
};

export const getBiogenicCO2WeightHeader = (epdBasedOnUnit?: EPDBasedOnUnitEnum) => {
  switch (epdBasedOnUnit) {
    case EPDBasedOnUnitEnum.DeclaredUnit:
      return (
        <span>
          Biogenic material kg CO<sub>2</sub>, eq./declared unit<sup>2</sup>
        </span>
      );
    case EPDBasedOnUnitEnum.FunctionalUnit:
      return (
        <span>
          Biogenic material kg CO<sub>2</sub>, eq./product<sup>2</sup>
        </span>
      );
    default:
      return (
        <span>
          Biogenic material kg CO<sub>2</sub>, eq./declared unit or product<sup>2</sup>
        </span>
      );
  }
};

export const getBiogenicCarbonWeightHeaderPdf = (epdBasedOnUnit?: EPDBasedOnUnitEnum) => {
  switch (epdBasedOnUnit) {
    case EPDBasedOnUnitEnum.DeclaredUnit:
      return (
        <span>
          Biogenic material<sup>1</sup>, kg C/declared unit
        </span>
      );
    case EPDBasedOnUnitEnum.FunctionalUnit:
      return (
        <span>
          Biogenic material<sup>1</sup>, kg C/product
        </span>
      );
    default:
      return (
        <span>
          Biogenic material<sup>1</sup>, kg C/declared unit or product
        </span>
      );
  }
};

export const getBiogenicCO2WeightHeaderPdf = (epdBasedOnUnit?: EPDBasedOnUnitEnum) => {
  switch (epdBasedOnUnit) {
    case EPDBasedOnUnitEnum.DeclaredUnit:
      return (
        <span>
          Biogenic material kg CO<sub>2</sub>, eq./declared unit
        </span>
      );
    case EPDBasedOnUnitEnum.FunctionalUnit:
      return (
        <span>
          Biogenic material kg CO<sub>2</sub>, eq./product
        </span>
      );
    default:
      return (
        <span>
          Biogenic material kg CO<sub>2</sub>, eq./declared unit or product
        </span>
      );
  }
};
